import { Injectable, inject } from '@angular/core';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

import autoTable from 'jspdf-autotable';
import { PrintLogService } from '@otas/shared/ui/print-monitor/print-log.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalUtilService {

  private _api: string = null;
  private _port: string = null;
  private _bgColor: string = null;
  private _configPath: string = null;
  private _rootPath: string = null;
  private _loginImage: string = null;
  private _language: string = 'english';

  private malayalamFontBase64: string = null;
  private textImageCache: { [key: string]: any } = {};

  private invokeFn: any = null;
  private tauriReady = false;
  /** print.log writer (R45) — no-op outside the desktop build. */
  private readonly printLog = inject(PrintLogService);
  /** Resolves when the @tauri-apps/api dynamic import has finished (or was skipped). */
  private tauriInit: Promise<void>;

  constructor() {
    this.tauriInit = this.initTauriSafe();
    this.preloadFonts();
  }

  private async initTauriSafe() {
    if ((window as any).__TAURI__) {
      const tauri = await import('@tauri-apps/api/core');
      this.invokeFn = tauri.invoke;
      this.tauriReady = true;
    }
  }

  private isTauri(): boolean {
    return typeof (window as any).__TAURI__ !== 'undefined';
  }

  private async invoke(cmd: string, args?: any): Promise<any> {
    // The dynamic import in initTauriSafe races APP_INITIALIZER; waiting here
    // (instead of checking invokeFn immediately) closes the startup race that
    // produced spurious "Tauri not available" and left api/port/config unset.
    await this.tauriInit;
    if (this.invokeFn) {
      return this.invokeFn(cmd, args);
    }
    return Promise.reject(new Error('Tauri not available'));
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  private async preloadFonts() {
    try {
      const response = await fetch('assets/fonts/NotoSerifTeluguTamilKannadaDevanagariMalayalamRegular.ttf');
      const arrayBuffer = await response.arrayBuffer();
      this.malayalamFontBase64 = this.arrayBufferToBase64(arrayBuffer);
    } catch (e) {
      console.warn("Could not preload Malayalam TTF font for PDF", e);
    }
  }

  async initializeTauri() {
    if (this.isTauri()) {
      this._api = await this.invoke('ipc_request', { arg: 'api' });
      this._port = await this.invoke('ipc_request', { arg: 'port' });
      this._bgColor = await this.invoke('ipc_request', { arg: 'Bg_color' });
      this._configPath = await this.invoke('ipc_request', { arg: 'configPath' });
      this._rootPath = await this.invoke('ipc_request', { arg: 'rootPath' });
      this._loginImage = await this.invoke('ipc_request', { arg: 'loginImage' });
      this._language = await this.invoke('ipc_request', { arg: 'language' });
    }
  }

  get isTauriApp(): boolean {
    return this.isTauri();
  }

  get fsInstance() {
    return null;
  }

  get process() {
    return null;
  }
  get API() {
    if (this.isTauri()) return this._api === 'null' ? null : this._api;
    return null;
  }
  get PORT() {
    if (this.isTauri()) return this._port === 'null' ? null : this._port;
    return null;
  }
  get bgColor() {
    if (this.isTauri()) return this._bgColor;
    return null;
  }
  get configPath() {
    if (this.isTauri()) return this._configPath;
    return null;
  }
  get rootPath() {
    if (this.isTauri()) return this._rootPath;
    return null;
  }
  get loginImage() {
    if (this.isTauri()) return this._loginImage;
    return null;
  }
  get language() {
    if (this.isTauri()) return this._language;
    return 'english';
  }
  set language(val: string) {
    this._language = val;
    if (this.isTauri()) {
      this.writeFile('app.lang', val);
    }
  }
  async checkSystemDependencies() {
    if (this.isTauri()) {
      return await this.invoke('check_dependencies');
    }
    return { runtime_exists: true, engine_exists: true, is_server: false };
  }

  async writeFile(fileName: string, content: string) {
    if (this.isTauri()) {
      return await this.invoke('write_file', { fileName, content });
    }
  }
  async writePdfFile(fileName: string, content: string) {
    if (this.isTauri()) {
      return await this.invoke('write_pdf_file', { fileName, content });
    }
  }
  async startPrintService() {
    if (this.isTauri()) {
      try {
        return await this.invoke('start_print_service');
      } catch (e) {
        console.error("Failed to start print service:", e);
      }
    }
  }
  async writeFileInPath(folderPath: string, fileName: string, content: string) {
    if (this.isTauri()) {
      return await this.invoke('write_file_in_path', { folderPath, fileName, content });
    }
  }
  updatePrintConfigs(data: any) {
    if (this.isTauri()) {
      this.invoke('update_print_configs', { data });
      return;
    }
  }
  async sendMail(data: any) {
    // Emailing is now handled via Java Backend API.
    // This method is kept for API compatibility if needed.
    console.warn("GlobalUtilService.sendMail: Use ReportService.sendEmailReport instead.");
  }

  // invoicePrint(data: any) {
  //   return window.electronAPI.electronIpcSendSync('invoicePrint', data);
  // }
  // get ipcRenderer() {
  //   const ipcRenderer = this.isTauriApp ? window.require('electron').ipcRenderer : null;
  //   return ipcRenderer;
  // }
  IPCRendererOn(eventString: string, callBack) {
  }
  IPCRendererSend(eventString: string, ...params: any) {
    if (eventString === 'handlePrintRequest') {
      // R45: record the UI side of every print dispatch in the install-folder
      // print.log (the Rust shell logs config, methods and outcomes itself).
      this.printLog.logDispatchRequest(params[0], params[1] === true);
    }
    if (this.isTauri()) {
      if (eventString === 'handlePrintRequest') {
        const sessionLang = sessionStorage.getItem('language');
        if (sessionLang) {
          params.push(sessionLang);
        }
      }
      this.invoke('ipc_renderer_send', { eventString, params });
      return;
    }

    // WEB PLATFORM FALLBACK
    this.handleWebIPC(eventString, params);
  }

  private handleWebIPC(eventString: string, params: any[]) {
    switch (eventString) {
      case 'printdemo':
        if (params[0]) this.printWeb(params[0]);
        break;
      case 'downloadPDF':
        if (params[0]) this.downloadWeb(params[0], 'Report.pdf', 'application/pdf');
        break;
      case 'downloadXLSX':
        if (params[0]) this.downloadWeb(params[0], 'Report.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        break;
      case 'handlePrintRequest':
        // For web, we usually just trigger browser print
        window.print();
        break;
      default:
        console.warn(`Web IPC: ${eventString} not implemented for web platform.`);
    }
  }

  private printWeb(base64: string) {
    const blob = this.base64ToBlob(base64, 'application/pdf');
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.focus();
      // Browser handles print via PDF viewer usually
    }
  }

  private downloadWeb(base64: string, fileName: string, type: string) {
    const blob = this.base64ToBlob(base64, type);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private base64ToBlob(base64: string, type: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: type });
  }
  IPCRendererSendSync(channel: string, ...arg: any) {
    return null;
  }
  IPCRendererSendOn(channel: string, arg: any) {
  }
  IPCRendererOnce(eventString: string, callBack) {
  }
  IPCRendererRemoveListener(channel: string, callBack) {
  }
  IPCRendererRemoveAllListeners(channel: string) {
  }

  async captureTableAsVectorPDF(tableElement: HTMLTableElement, reportName: string, templeAddress: string, orgName: string, orientation: 'p' | 'l' = 'p'): Promise<string> {
    const pdf = new jsPDF(orientation, 'mm', 'a4');

    // Clear cache at the start of a new PDF generation to free memory
    this.textImageCache = {};

    // Register Malayalam Font if available
    if (this.malayalamFontBase64) {
      pdf.addFileToVFS('NotoSerif.ttf', this.malayalamFontBase64);
      pdf.addFont('NotoSerif.ttf', 'NotoSerif', 'normal');
    }

    // Set fallback font
    pdf.setFont(this.malayalamFontBase64 ? 'NotoSerif' : 'helvetica', 'normal');

    const pageWidth = pdf.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;

    // Add Header
    this.drawMalayalamHeader(pdf, orgName || 'SHIVAM', centerX, 15, 16, true);
    this.drawMalayalamHeader(pdf, templeAddress || '', centerX, 22, 12, false);
    this.drawMalayalamHeader(pdf, reportName || 'Report', centerX, 30, 14, true);

    // Generate Table
    const totalPagesExp = '{total_pages_count_string}';
    autoTable(pdf, {
      html: tableElement,
      startY: 35,
      margin: { bottom: 25 }, // Give ample space for the footer signatures
      alternateRowStyles: {
        fillColor: [255, 255, 255] // Completely remove default gray alternate striping
      },
      styles: {
        font: this.malayalamFontBase64 ? 'NotoSerif' : 'helvetica',
        fontSize: 9,
        cellPadding: 2,
        lineColor: [0, 0, 0], // Pure black borders for dot matrix visibility
        lineWidth: 0, // Remove all grid lines for much faster dot matrix printing
        textColor: [0, 0, 0] // Pure black text
      },
      headStyles: {
        fillColor: [255, 255, 255], // Pure white (no background) to prevent illegible halftone dithering
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: { top: 0.5, bottom: 0.5, left: 0, right: 0 } // Only top and bottom borders for the header
      },
      willDrawCell: (data) => {
        const el = data.cell.raw as HTMLElement;
        const angleAttr = el && el.getAttribute ? el.getAttribute('data-angle') : null;

        if (data.cell.raw && data.cell.text && data.cell.text.length > 0) {
          const textStr = data.cell.text.join(' ');
          if (/[\u0D00-\u0D7F]/.test(textStr) || angleAttr) {
            // Save original text for didDrawCell
            (data.cell.raw as any)._customText = textStr;
            (data.cell.raw as any)._angle = angleAttr ? parseInt(angleAttr, 10) : 0;
            // Clear text so autoTable doesn't draw it brokenly
            data.cell.text = [];
          }
        }
      },
      didDrawCell: (data) => {
        if (data.cell.raw && (data.cell.raw as any)._customText) {
          const textStr = (data.cell.raw as any)._customText;
          const angle = (data.cell.raw as any)._angle || 0;
          const isBold = data.cell.styles.fontStyle === 'bold';

          // Extract rgb array from style if possible, else default black
          let color = [0, 0, 0];
          if (Array.isArray(data.cell.styles.textColor)) {
            color = data.cell.styles.textColor as number[];
          }

          const padding = 2; // matches cellPadding in styles
          let x = data.cell.x + padding;
          let y = data.cell.y + padding;

          // If angled text, anchor it at the bottom left of the cell so it rotates upwards
          if (angle) {
            // Anchor at the middle of the cell horizontally, near the bottom
            y = data.cell.y + data.cell.height - padding - 1;
            x = data.cell.x + (data.cell.width / 2) - 2;
          }

          if (/[\u0D00-\u0D7F]/.test(textStr)) {
            const imgInfo = this.renderMalayalamTextToCanvas(textStr, data.cell.styles.fontSize, isBold, color);

            let renderX = x - imgInfo.offsetX;
            let renderY = y - imgInfo.offsetY;

            if (angle) {
              pdf.addImage(imgInfo.dataUrl, 'PNG', renderX, renderY, imgInfo.widthMm, imgInfo.heightMm, imgInfo.alias, 'FAST', angle);
            } else {
              pdf.addImage(imgInfo.dataUrl, 'PNG', renderX, renderY, imgInfo.widthMm, imgInfo.heightMm, imgInfo.alias, 'FAST');
            }
          } else {
            pdf.setFontSize(data.cell.styles.fontSize);
            pdf.setFont(data.cell.styles.font, data.cell.styles.fontStyle);
            pdf.setTextColor(color[0], color[1], color[2]);
            pdf.text(textStr, x, y, { angle: angle });
          }
        }
      },
      didParseCell: function (data) {
        const el = data.cell.raw as HTMLElement;

        if (el && el.getAttribute) {
          const angleAttr = el.getAttribute('data-angle');
          if (angleAttr) {
            data.cell.styles.minCellHeight = 35; // Expand height for angled text
            data.cell.styles.valign = 'bottom';
          }
        }

        // Preserve background color of group headers or specific rows
        if (el && el.parentElement) {
          const bgColor = window.getComputedStyle(el.parentElement).backgroundColor;
          if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
            // Dot matrix optimization: No gray backgrounds, just use bold text for emphasis
            if (el.parentElement.style.backgroundColor === 'rgb(234, 237, 237)' || el.parentElement.style.backgroundColor === '#EAEDED') {
              data.cell.styles.fillColor = [255, 255, 255]; // Pure white
              data.cell.styles.textColor = [0, 0, 0];
              data.cell.styles.fontStyle = 'bold';
              // Explicitly use helvetica for English grouped rows to guarantee true bold
              data.cell.styles.font = 'helvetica';
            }
          }
        }
      },
      didDrawPage: (data) => {
        const pageSize = pdf.internal.pageSize;
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();

        // Draw Horizontal Line
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0, 0, 0);
        pdf.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12);

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');

        // Render Date Created
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        let hr = now.getHours();
        const min = String(now.getMinutes()).padStart(2, '0');
        const ampm = hr >= 12 ? 'pm' : 'am';
        hr = hr % 12;
        hr = hr ? hr : 12;
        const hrStr = String(hr).padStart(2, '0');
        const dateStr = `${dd}/${mm}/${yyyy} ${hrStr}:${min} ${ampm}`;
        pdf.text(dateStr, 14, pageHeight - 7);

        // Render Page Numbers
        const str = 'PAGE ' + data.pageNumber + ' OF ' + totalPagesExp;
        pdf.text(str, pageWidth - 14, pageHeight - 7, { align: 'right' });
      }
    });

    // Draw Signatures ONLY on the last page
    const totalPages = (pdf as any).internal.getNumberOfPages();
    pdf.setPage(totalPages);

    const pageSize = pdf.internal.pageSize;
    const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
    const pageWidthSigs = pageSize.width ? pageSize.width : pageSize.getWidth();

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    const sigs = ["OFFICE CLERK", "SECRETARY", "PRESIDENT", "TREASURER", "AUDITOR"];
    const ySigs = pageHeight - 16;

    for (let i = 0; i < sigs.length; i++) {
      const x = 14 + (i * ((pageWidthSigs - 28) / 4));
      if (i === 0) pdf.text(sigs[i], x, ySigs, { align: 'left' });
      else if (i === sigs.length - 1) pdf.text(sigs[i], x, ySigs, { align: 'right' });
      else pdf.text(sigs[i], x, ySigs, { align: 'center' });
    }

    if (typeof pdf.putTotalPages === 'function') {
      pdf.putTotalPages(totalPagesExp);
    }

    const resultPdf = pdf.output('datauristring').split(',')[1];

    // Clear cache to prevent memory leaks
    this.textImageCache = {};

    return resultPdf;
  }

  private drawMalayalamHeader(pdf: jsPDF, text: string, xPos: number, yPos: number, fontSize: number, isBold: boolean) {
    if (/[\u0D00-\u0D7F]/.test(text)) {
      const imgInfo = this.renderMalayalamTextToCanvas(text, fontSize, isBold, [0, 0, 0]);
      // Center align:
      const x = xPos - (imgInfo.widthMm / 2); // Horizontal padding cancels out perfectly for center align
      const actualHeight = imgInfo.heightMm - (imgInfo.offsetY * 2);
      const y = yPos - (actualHeight * 0.75) - imgInfo.offsetY; // alignment adjustment for baseline
      pdf.addImage(imgInfo.dataUrl, 'PNG', x, y, imgInfo.widthMm, imgInfo.heightMm, imgInfo.alias, 'FAST');
    } else {
      pdf.setFontSize(fontSize);
      pdf.setFont(isBold ? 'helvetica' : 'helvetica', isBold ? 'bold' : 'normal');
      pdf.text(text, xPos, yPos, { align: 'center' });
    }
  }

  private renderMalayalamTextToCanvas(text: string, fontSize: number, isBold: boolean, color: number[]): { dataUrl: string, widthMm: number, heightMm: number, offsetX: number, offsetY: number, alias: string } {
    const cacheKey = `img_${fontSize}_${isBold}_${color.join(',')}_${text}`;
    if (this.textImageCache[cacheKey]) {
      return this.textImageCache[cacheKey];
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    const scale = 4;
    const fontSizePx = Math.ceil((fontSize * 1.333) * scale);
    const fontStr = `${isBold ? 'bold' : 'normal'} ${fontSizePx}px "Noto Sans Malayalam", "Noto Serif Malayalam", Arial, sans-serif`;

    ctx.font = fontStr;
    const metrics = ctx.measureText(text);

    const textWidth = Math.ceil(metrics.width);
    const textHeight = Math.ceil(fontSizePx * 1.5);

    // Add generous padding to avoid clipping ascenders/descenders/loops
    const padPx = Math.ceil(fontSizePx * 0.4);

    canvas.width = textWidth + (padPx * 2) || 1;
    canvas.height = textHeight + (padPx * 2) || 1;

    ctx.font = fontStr;
    ctx.fillStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
    ctx.textBaseline = 'top';

    // Draw text with padding offset
    ctx.fillText(text, padPx, padPx + (fontSizePx * 0.1));

    const pxToMm = 0.264583;
    const widthMm = (canvas.width / scale) * pxToMm;
    const heightMm = (canvas.height / scale) * pxToMm;
    const offsetMm = (padPx / scale) * pxToMm;

    const result = { dataUrl: canvas.toDataURL('image/png'), widthMm, heightMm, offsetX: offsetMm, offsetY: offsetMm, alias: cacheKey };
    this.textImageCache[cacheKey] = result;

    return result;
  }

  /**
   * Glyph Fixing Algorithm for Malayalam
   * jsPDF does not support Complex Text Layout (CTL). This algorithm manually re-positions
   * left-sided and split vowel signs around consonant clusters to approximate visual rendering.
   */
  private fixMalayalamGlyphs(text: string): string {
    if (!text || !/[\u0D00-\u0D7F]/.test(text)) return text;

    // Malayalam consonants & chillu
    const cons = '[\u0D15-\u0D3A\u0D7A-\u0D7F]';
    const virama = '\u0D4D';
    const zwj = '\u200D';
    const zwnj = '\u200C';
    // A consonant cluster: (consonant + virama)* + consonant
    const cluster = `(?:${cons}(?:${virama}|${zwj}|${zwnj})+)*${cons}`;

    // Left-sided vowels: e, E, ai
    const leftVowels = '([\u0D46\u0D47\u0D48])';
    const leftRegex = new RegExp(`(${cluster})${leftVowels}`, 'g');

    // Split vowels: o, O, au
    const splitVowels = '([\u0D4A\u0D4B\u0D4C])';
    const splitRegex = new RegExp(`(${cluster})${splitVowels}`, 'g');

    let result = text;

    // 1. Decompose and reorder split vowels
    result = result.replace(splitRegex, (match, cl, vowel) => {
      if (vowel === '\u0D4A') return '\u0D46' + cl + '\u0D3E'; // o -> e + aa
      if (vowel === '\u0D4B') return '\u0D47' + cl + '\u0D3E'; // O -> E + aa
      if (vowel === '\u0D4C') return '\u0D46' + cl + '\u0D57'; // au -> e + au_length_mark
      return match;
    });

    // 2. Reorder left vowels
    result = result.replace(leftRegex, '$2$1');

    return result;
  }

  async captureAsPDF(): Promise<string> {
    const element = document.querySelector('section.widget') as HTMLElement;
    if (!element) {
      throw new Error("Report content not found (expected 'section.widget')");
    }

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      allowTaint: true,
      ignoreElements: (el) =>
        el.classList.contains('hidden-print') ||
        el.classList.contains('d-print-none') ||
        el.getAttribute('data-html2canvas-ignore') === 'true'
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    return pdf.output('datauristring').split(',')[1];
  }

  async captureAsExcel(): Promise<string> {
    const XLSX = (window as any).XLSX;
    if (!XLSX) {
      throw new Error("XLSX library not loaded");
    }

    const section = document.querySelector('section.widget') as HTMLElement;
    if (!section) {
      throw new Error("Report section not found (expected 'section.widget')");
    }

    const aoa: any[][] = []; // array-of-arrays for the sheet

    // 1. Extract report header (temple name, address, report title)
    const reportHeader = section.querySelector('app-report-header, .app-report-header, header');
    if (reportHeader) {
      const h2 = reportHeader.querySelector('h2');
      if (h2 && h2.textContent?.trim()) {
        aoa.push([h2.textContent.trim()]);
      }
      const h4s = reportHeader.querySelectorAll('h4');
      h4s.forEach(h4 => {
        const text = h4.textContent?.trim();
        if (text) {
          aoa.push([text]);
        }
      });
      const h5 = reportHeader.querySelector('h5');
      if (h5 && h5.textContent?.trim()) {
        aoa.push([h5.textContent.trim()]);
      }
      aoa.push([]); // blank row separator
    }

    // 2. Process the table including group headers
    const table = section.querySelector('table') as HTMLTableElement;
    if (!table) {
      throw new Error("Table content not found in section");
    }

    // Walk all direct children of <table> (thead, tbody elements)
    const children = table.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      const tag = child.tagName.toLowerCase();

      if (tag === 'thead') {
        // Check if this is a group header (class thead-default or secondary thead)
        const rows = child.querySelectorAll('tr');
        if (rows.length > 0) {
          rows.forEach(row => {
            const cells = row.querySelectorAll('th, td');
            const rowData: any[] = [];
            cells.forEach(cell => {
              const text = (cell as HTMLElement).textContent?.trim() || '';
              if (text) rowData.push(text);
            });
            if (rowData.length > 0) {
              aoa.push(rowData);
            }
          });
        } else {
          // thead without <tr> — direct <th> children (group header pattern)
          const ths = child.querySelectorAll('th');
          const rowData: any[] = [];
          ths.forEach(th => {
            const text = th.textContent?.trim() || '';
            rowData.push(text);
          });
          if (rowData.some(v => v !== '')) {
            aoa.push(rowData);
          }
        }
      } else if (tag === 'tbody') {
        const rows = child.querySelectorAll('tr');
        rows.forEach(row => {
          const cells = row.querySelectorAll('td, th');
          const rowData: any[] = [];
          cells.forEach(cell => {
            const text = (cell as HTMLElement).textContent?.trim() || '';
            // Try to parse numbers
            const num = parseFloat(text.replace(/,/g, ''));
            if (!isNaN(num) && text.match(/^[\d,]+\.?\d*$/)) {
              rowData.push(num);
            } else {
              rowData.push(text);
            }
          });
          aoa.push(rowData);
        });
      }
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const base64 = btoa(new Uint8Array(excelBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));
    return base64;
  }
}

